/* eslint-disable max-len */
/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import goals from './modules/goals';
import budgets from './modules/budgets';

Vue.use(Vuex);

const safeParseJSON = (value, fallback = {}) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const normalizeUserTypes = (primaryType, allTypes) => {
  if (Array.isArray(allTypes) && allTypes.length) return allTypes;
  return primaryType ? [primaryType] : [];
};

const getEffectiveTypes = (types) => {
  if (types.includes('smart-steward') && !types.includes('smart-mina')) {
    return [...types, 'smart-mina'];
  }
  return types;
};

const decodeTokenSafely = (token) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    return {};
  }
};

export default new Vuex.Store({
  state: {
    status: '',
    token: localStorage.getItem('token') || '',
    user: {},
    user_type: null,
    user_types: [],
    selectedUserType: (() => {
      const stored = localStorage.getItem('selectedUserType');
      const parsedUser = safeParseJSON(localStorage.getItem('user'), {});
      const primary = parsedUser.user_type || null;
      const types = normalizeUserTypes(primary, parsedUser.user_types);

      if (stored && getEffectiveTypes(types).includes(stored)) return stored;
      return primary || stored || null;
    })(),
    bank: safeParseJSON(localStorage.getItem('bank'), {}),
  },
  mutations: {
    auth_request(state) {
      state.status = 'loading';
    },
    auth_success(state, payload) {
      const {
        token, user, user_type, user_types, bank,
      } = payload || {};
      state.status = 'success';
      state.token = token || state.token;
      state.user = user || state.user;
      if (bank) {
        state.bank = bank;
        localStorage.setItem('bank', JSON.stringify(bank));
      }
      const primary = user_type || (user && user.user_type) || null;
      const types = normalizeUserTypes(primary, user_types || (user && user.user_types));
      state.user_type = primary;
      state.user_types = types;
      const stored = localStorage.getItem('selectedUserType');
      state.selectedUserType = (stored && getEffectiveTypes(state.user_types).includes(stored)) ? stored : primary;
      if (state.selectedUserType) {
        localStorage.setItem('selectedUserType', state.selectedUserType);
      }
    },
    set_bank(state, bank) {
      state.bank = bank;
      localStorage.setItem('bank', JSON.stringify(bank));
    },
    auth_error(state) {
      state.status = 'error';
    },
    set_selected_user_type(state, type) {
      const effectiveTypes = [...state.user_types];
      if (effectiveTypes.includes('smart-steward') && !effectiveTypes.includes('smart-mina')) {
        effectiveTypes.push('smart-mina');
      }
      if (effectiveTypes.includes(type)) {
        if (state.selectedUserType === type) return;
        state.selectedUserType = type;
        localStorage.setItem('selectedUserType', type);
      } else if (state.user_type) {
        if (state.selectedUserType === state.user_type) return;
        state.selectedUserType = state.user_type;
        localStorage.setItem('selectedUserType', state.user_type);
      }
    },
    add_user_type(state, type) {
      if (!state.user_types.includes(type)) {
        state.user_types = [...state.user_types, type];
        const user = safeParseJSON(localStorage.getItem('user'), {});
        user.user_types = state.user_types;
        localStorage.setItem('user', JSON.stringify(user));
      }
      state.selectedUserType = type;
      localStorage.setItem('selectedUserType', type);
    },
    logout(state) {
      state.status = '';
      state.token = '';
      state.user = {};
      state.user_type = null;
      state.user_types = [];
      state.selectedUserType = null;
      state.bank = {};
    },
  },
  actions: {
    login({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit('auth_request');
        let url = '';
        if (window.location.hostname === 'localhost') {
          url = 'http://localhost:8081/login';
        } else {
          url = 'https://smart-stewards.herokuapp.com/login';
        }
        axios({
          url,
          data: user,
          method: 'POST',
        })
          .then((res) => {
            const {
              token,
              riskAppetite,
              bank,
              user_type: resUserType,
              user_types: resUserTypes,
            } = res.data;
            const decoded = jwtDecode(token);
            const user_type = resUserType ?? decoded.user_type ?? null;
            const user_types = Array.isArray(resUserTypes) && resUserTypes.length
              ? resUserTypes
              : (decoded.user_types || (user_type ? [user_type] : []));
            const userPayload = {
              ...decoded,
              user_type,
              user_types,
            };
            localStorage.setItem('token', token);
            localStorage.setItem('bank', JSON.stringify(bank));
            localStorage.setItem('user', JSON.stringify(userPayload));
            localStorage.setItem('risk_appetite', riskAppetite);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            commit('auth_success', {
              token,
              user: userPayload,
              user_type,
              user_types,
              bank,
            });
            resolve(res);
          })
          .catch((err) => {
            commit('auth_error');
            localStorage.removeItem('token');
            reject(err);
          });
      });
    },

    register({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit('auth_request');
        let url = '';
        if (window.location.hostname === 'localhost') {
          url = 'http://localhost:8081/register';
        } else {
          url = 'https://smart-stewards.herokuapp.com/register';
        }
        axios({ url, data: user, method: 'POST' })
          .then((res) => {
            const { token } = res.data;
            const decoded = jwtDecode(token);
            const user_type = decoded.user_type ?? null;
            const user_types = Array.isArray(decoded.user_types) && decoded.user_types.length ? decoded.user_types : (user_type ? [user_type] : []);
            const userPayload = { ...decoded, user_type, user_types };
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userPayload));
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            commit('auth_success', {
              token, user: userPayload, user_type, user_types,
            });
            resolve(res);
          })
          .catch((err) => {
            commit('auth_error', err);
            localStorage.removeItem('token');
            reject(err.response);
          });
      });
    },

    logout({ commit }) {
      return new Promise((resolve) => {
        commit('logout');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedUserType');
        delete axios.defaults.headers.common.Authorization;
        resolve();
      });
    },

    async optInToSmartMina({ commit, state }) {
      if (state.user_types.includes('smart-mina')) {
        commit('set_selected_user_type', 'smart-mina');
        return;
      }
      const baseURL = window.location.hostname === 'localhost'
        ? 'http://localhost:8081'
        : 'https://smart-stewards.herokuapp.com';
      await axios.post(`${baseURL}/user/type`, { user_type: 'smart-mina' }, {
        headers: { Authorization: `Bearer ${state.token}` },
      });
      commit('add_user_type', 'smart-mina');
    },

    initAuth({ commit }) {
      const token = localStorage.getItem('token');
      if (!token) return;

      const user = safeParseJSON(localStorage.getItem('user'), {});
      const decoded = Object.keys(user).length ? {} : decodeTokenSafely(token);
      const userPayload = Object.keys(user).length ? user : decoded;
      const user_type = userPayload.user_type ?? null;
      const user_types = normalizeUserTypes(user_type, userPayload.user_types);
      commit('auth_success', {
        token,
        user: userPayload,
        user_type,
        user_types,
        bank: safeParseJSON(localStorage.getItem('bank'), {}),
      });
    },
  },
  getters: {
    isLoggedIn: (state) => !!state.token,
    authStatus: (state) => state.status,
    user_type: (state) => state.user_type,
    user_types: (state) => {
      let types = state.user_types && state.user_types.length
        ? [...state.user_types]
        : (state.user_type ? [state.user_type] : []);
      // Ensure the primary user_type is always represented even if the
      // user_types array from the backend doesn't include it
      if (state.user_type && !types.includes(state.user_type)) {
        types = [...types, state.user_type];
      }
      if (types.includes('smart-steward') && !types.includes('smart-mina')) {
        return [...types, 'smart-mina'];
      }
      return types;
    },
    selectedUserType: (state, getters) => {
      const type = state.selectedUserType;
      const types = getters.user_types;
      if (type && types.includes(type)) return type;
      const primary = state.user_type || (state.user && state.user.user_type);
      if (primary) return primary;
      return type || null;
    },
    hasMultipleUserTypes: (state, getters) => (getters.user_types && getters.user_types.length > 1),
    currentBank: (state, getters) => {
      const selectedType = getters.selectedUserType;
      const bank = state.bank || {};
      if (bank[selectedType]) {
        return {
          accountName: bank.accountName,
          ...bank[selectedType],
        };
      }
      return bank;
    },
  },
  modules: {
    goals,
    budgets,
  },
});
